/**
 * Help Article Editor Component
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { 
  Save, X, Monitor, Smartphone, Eye, EyeOff, 
  ChevronUp, ChevronDown, Trash2, Edit 
} from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';

const getStatusBadge = (status) => {
  const styles = {
    published: 'bg-green-500/20 text-green-400 border-green-500/30',
    draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };
  return styles[status] || styles.draft;
};

export const ArticleCard = ({
  section,
  isEditing,
  editForm,
  setEditForm,
  onStartEdit,
  onSave,
  onCancel,
  onDelete,
  onTogglePublish,
  onReorder,
  isFirst,
  isLast
}) => {
  const { isDark } = useTheme();
  const [previewMode, setPreviewMode] = useState('desktop');

  if (isEditing) {
    return (
      <Card className={`${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
        <CardContent className="p-4">
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Title
              </label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className={`mt-1 ${isDark ? 'bg-gray-900 border-gray-600 text-white' : ''}`}
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={previewMode === 'desktop' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewMode('desktop')}
                className={previewMode === 'desktop' ? 'bg-yellow-400 text-black' : ''}
              >
                <Monitor className="w-4 h-4 mr-1" />
                Desktop
              </Button>
              <Button
                variant={previewMode === 'mobile' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewMode('mobile')}
                className={previewMode === 'mobile' ? 'bg-yellow-400 text-black' : ''}
              >
                <Smartphone className="w-4 h-4 mr-1" />
                Mobile
              </Button>
            </div>

            {previewMode === 'desktop' ? (
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Content (Desktop)
                </label>
                <Textarea
                  value={editForm.content}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                  rows={10}
                  className={`mt-1 font-mono text-sm ${isDark ? 'bg-gray-900 border-gray-600 text-white' : ''}`}
                  placeholder="Markdown supported..."
                />
              </div>
            ) : (
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Content (Mobile - Shorter)
                </label>
                <Textarea
                  value={editForm.mobile_content}
                  onChange={(e) => setEditForm({ ...editForm, mobile_content: e.target.value })}
                  rows={6}
                  className={`mt-1 font-mono text-sm ${isDark ? 'bg-gray-900 border-gray-600 text-white' : ''}`}
                  placeholder="Shorter version for mobile..."
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Status
                </label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-gray-900 border-gray-600 text-white' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Tags (comma-separated)
                </label>
                <Input
                  value={editForm.tags?.join(', ') || ''}
                  onChange={(e) => setEditForm({ 
                    ...editForm, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                  })}
                  className={`mt-1 ${isDark ? 'bg-gray-900 border-gray-600 text-white' : ''}`}
                  placeholder="getting-started, account"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onCancel}>
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => onSave(section.id)}>
                <Save className="w-4 h-4 mr-1" />
                Save Changes
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // View Mode
  return (
    <Card 
      className={`${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} ${
        section.status === 'draft' ? 'opacity-70' : ''
      }`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {section.title}
              </h3>
              <Badge className={`${getStatusBadge(section.status)} border`}>
                {section.status}
              </Badge>
            </div>
            <p className={`text-sm line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {section.content?.slice(0, 150)}...
            </p>
            {section.tags?.length > 0 && (
              <div className="flex gap-1 mt-2">
                {section.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-1 ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReorder(section.id, 'up')}
              disabled={isFirst}
              className="text-gray-400 hover:text-white"
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReorder(section.id, 'down')}
              disabled={isLast}
              className="text-gray-400 hover:text-white"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onTogglePublish(section)}
              className="text-gray-400 hover:text-white"
            >
              {section.status === 'published' ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onStartEdit(section)}
              className="text-gray-400 hover:text-white"
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(section.id)}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ArticleCard;
